import { create } from 'zustand';
import {
  serverMessageSchema,
  type Bucket,
  type ChangeEvent,
  type Command,
  type FilterCondition,
  type ProjectPrefs,
  type ViewGrouping,
  type PresenceMember,
  type ProjectRole,
  type ProjectState,
  type ProjectSummary,
  type User,
  canWrite,
  scenarioIdOf,
  type NodeDisplay,
  type PptxGeom,
  type Perspective,
  type Stage,
} from '@integrate/contracts';
import {
  applyCommand,
  decisionsFromBuckets,
  projectDecisionWrites,
  touchesNode,
  type CommandSlice,
  type SliceEmployee,
  type SeparationState,
} from '@integrate/core';
import { api } from './api.js';
import { lensForPhase, resolvePhase, type PhaseId, type PhaseLens } from './phases.js';

/**
 * This tab's lens: the phase-decided fields plus the filters, which outlive a phase change.
 *
 * Spelled as `PhaseLens & { filters }` rather than restated, so a field added to one is a type error
 * in the other rather than a value silently dropped on the next phase click.
 */
export type StoreLens = PhaseLens & {
  filters: FilterCondition[];
  /** 'hide' removes out-of-scope people; 'grey' keeps them visible but dimmed. */
  viewMode: 'hide' | 'grey';
  viewLayout: 'grouping' | 'reporting';
  /** A custom pivot replacing the canonical hierarchy, for viewing only. */
  viewGrouping: ViewGrouping;
};

/**
 * The lens fields that outlive a phase change and persist to the preferences row.
 *
 * Named as a list rather than inferred, because both users of the fact are easy to get wrong
 * separately: `setPhase` has to carry exactly these across, and `setLens` has to notice exactly these
 * to schedule a save. A pivot set on its own used to reach neither.
 */
const PERSISTED_LENS_KEYS = ['filters', 'viewMode', 'viewLayout', 'viewGrouping'] as const;

/**
 * How far back Ctrl-Z looks for a change of yours.
 *
 * Small on purpose. This is "take back what I just did", not a search: if your last fifty changes are all
 * somebody else's or all blocked, the gesture is not the tool and the History modal — which pages, filters
 * by author and explains each refusal — is.
 */
const UNDO_SEARCH_LIMIT = 50;

/**
 * The lens as a preferences PATCH — and deliberately WITHOUT `activeScenarioId`.
 *
 * The route patches field by field, so omitting it leaves the stored scenario alone. Including it would
 * mean every debounced filter save also restated which scenario this tab is in, and a save racing a
 * switch would put the old one back.
 */
/** The half of the preferences row that IS the lens — everything except which scenario you are in. */
function persistedPrefsOf(prefs: ProjectPrefs): Omit<ProjectPrefs, 'activeScenarioId'> {
  const { activeScenarioId: _scenario, ...lens } = prefs;
  return lens;
}

function persistedLens(lens: StoreLens): Omit<ProjectPrefs, 'activeScenarioId'> {
  return {
    filters: lens.filters,
    viewMode: lens.viewMode,
    viewLayout: lens.viewLayout,
    viewGrouping: lens.viewGrouping,
  };
}

/**
 * `denied` is TERMINAL, and it is the only one of these that is.
 *
 * The other five all mean "this will resolve itself, keep waiting". Being thrown out of a project does
 * not resolve itself: the project was deleted, or somebody took your access away, and retrying cannot
 * change either. Without a state that says so the tab had nowhere to stop — see `scheduleReconnect`.
 */
type ConnectionStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'offline' | 'denied';

interface PendingCommand {
  command: Command;
  /** The decisions snapshot to restore if the server rejects this. */
  rollbackTo: Record<string, Bucket>;
  /**
   * Sent over a socket that has since died, so its ack can never arrive.
   *
   * `cid` is scoped to one connection — it exists only in the `cmd`/`ack`/`reject` frames and appears on
   * no event — so once that socket is gone the entry is unsettleable by any later frame. Without this
   * flag it simply stayed in `pending` for the life of the tab, and `replayPending` re-applied it on top
   * of every subsequent resync AND every applied remote event: a value this tab could no longer save,
   * silently overriding the server's, including a colleague's later change to the same person, with the
   * pill reading "Live".
   */
  orphaned?: boolean;
}

interface AppState {
  user: User | null;
  /**
   * Whether this DEPLOYMENT has an assistant at all, learned once at sign-in.
   *
   * Held rather than asked per project because it is a property of the server's configuration, not of
   * the project. The alternative — offering the box and refusing what somebody types into it — is a
   * worse experience than not offering it, and is exactly what `azureChatConfig` returning null was
   * meant to avoid rather than produce.
   */
  assistantAvailable: boolean;
  /**
   * Whether this deployment can TRANSCRIBE, which is a different question from having an assistant.
   *
   * On Azure a deployment names one model and speech-to-text is not the chat model, so a tenant can
   * perfectly well have the box and no microphone. Folding the two into one flag would put a control on
   * screen whose only outcome is a refusal — the exact thing the flag above exists to prevent.
   *
   * The browser has a veto of its own, applied where the button is rendered: it must also be able to
   * RECORD a container the service reads, which only the browser can answer.
   */
  voiceAvailable: boolean;
  /**
   * Whether a live spoken session can be held.
   *
   * A THIRD FLAG, not a detail of `voiceAvailable`: transcription and realtime are two Azure
   * deployments and a tenant may have either, both or neither.
   */
  realtimeAvailable: boolean;
  bootstrapped: boolean;
  projects: ProjectSummary[];

  project: ProjectState | null;
  /**
   * The full design, lens-free.
   *
   * Perspective, stage and filters are NOT in here — and now cannot be: `SeparationState` has no such
   * fields, so this is structural rather than a promise this comment makes. All three are per-user, they
   * live in `lens` below, and they never become commands. While the first two were still on the state
   * they were typed, non-optional and permanently 'as-is'/'day1', so a panel reaching for
   * `design.perspective` instead of its `perspective` prop got a plausible wrong answer with no error.
   */
  design: SeparationState | null;
  nodeDisplay: NodeDisplay | null;
  /** Slide title and content geometry. Project state like nodeDisplay, and edited beside it. */
  pptxGeom: PptxGeom | null;
  /**
   * Which scenario the design above belongs to.
   *
   * Target placements are per-scenario — a deliberate divergence from the app, which shares one set
   * across every scenario — so "118 of 418 placed" is only meaningful attributed to one. The payload
   * has carried it since P3; nothing had needed it until the Target phase.
   */
  scenarioName: string | null;
  /**
   * This tab's view of the project. Nobody else's, and nothing the server records.
   *
   * `filters` belongs here rather than on the fetched design: narrowing to one country is a way of
   * reading, not a change to what is being read. It is persisted per (user, project) so it survives a
   * reload, but through a preferences endpoint that emits no event and broadcasts nothing.
   */
  lens: StoreLens;
  /**
   * Which workflow phase this tab is working in — also per-user, also never a command.
   *
   * It carries the lens (selecting a phase sets the natural perspective and stage for that work) but is
   * not derivable from it: Data and Target imply the same lens and are different places to be.
   */
  phase: PhaseId;
  decisions: Record<string, Bucket>;
  seq: number;
  role: ProjectRole | null;

  members: PresenceMember[];
  connectionId: string | null;
  status: ConnectionStatus;
  activity: ChangeEvent[];
  pending: Record<string, PendingCommand>;
  error: string | null;
  notice: string | null;
  /**
   * Why this tab was thrown out, when it was. Null at every other time.
   *
   * ITS OWN FIELD RATHER THAN `error`, which is the transient banner a rejected command writes and the
   * next action clears. This one must not be clearable: the sentence is the entire explanation for a tab
   * that has stopped working permanently, and an unrelated dismiss taking it away would leave a dead
   * screen with no account of itself — which is the state this whole change exists to remove.
   */
  denied: string | null;
  /**
   * Somebody else just changed the node this tab has open.
   *
   * Its own field rather than `notice`, which is the global banner: this belongs beside the panel that is
   * now describing something that moved, and the two can be true at once — a reconnect notice and a
   * colleague's edit are different news.
   *
   * Set from `applyRemoteEvent` BEFORE it branches. That is load-bearing: an affordance written only in
   * the success branch misses every rename, head designation, TSA, data update and target move, because
   * those take the resync branch; one written inside `resync` fires on this tab's own dispatch and revert,
   * which also resync and carry no actor to attribute to.
   */
  conflict: { actorName: string; summary: string; seq: number; nodeId: string } | null;
  /**
   * Somebody replaced the whole project under this tab.
   *
   * Its own field rather than `notice` or `conflict`, because it is a different KIND of news from either.
   * `notice` is about this tab's connection; `conflict` is about the one object you have open and sits
   * beside it. A restore is about everything at once — the person you were looking at may not be in the
   * roster any more, the scenario you were in may have gone or come back — so the only honest place for
   * it is the top of the screen.
   *
   * Nothing here offers a way back, deliberately. The design has already changed, the change is already
   * in the log, and the only real remedy is another restore, which is owner-only and typed-confirmed. A
   * button here would be an undo the server refuses by design.
   */
  restored: { actorName: string; summary: string; seq: number } | null;

  bootstrap(): Promise<void>;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  /**
   * Take the account back from a write that changed it — today, your own name.
   *
   * A setter rather than a fetch: `AccountModal` has just been handed the updated `User` by the
   * route, so re-asking `/api/me` for it would be a second round trip to learn what is already in
   * hand. It is the header, the presence strip and every "you" on screen that need to catch up.
   */
  applyOwnProfile(user: User): void;
  loadProjects(): Promise<void>;
  importProject(file: File, name: string): Promise<string>;
  openProject(projectId: string): Promise<void>;
  setLens(lens: Partial<StoreLens>): void;
  /**
   * Open a different scenario, without leaving the project.
   *
   * PER USER, which is the whole point: the preference row records it, so two people on one project can
   * be comparing two carve-out options at the same time rather than taking turns. The socket is not
   * touched — it is per project, `seq` is per project, and the change log is shared — so this is a
   * refetch and not a reconnect.
   */
  setScenario(scenarioId: string): Promise<void>;
  setPhase(phase: PhaseId): void;
  closeProject(): void;
  setBucket(employeeId: string, bucket: Bucket | null): void;
  /** One command, or several as one atomic change. */
  /**
   * Run a change, and say whether it landed.
   *
   * RESOLVES EITHER WAY — a refusal is surfaced through the error banner rather than thrown, because
   * most callers are a button that has already done its job by the time one arrives. But it now also
   * RETURNS whether it committed, because some callers cannot be written correctly without knowing: the
   * roster refresh reports "the roster was refreshed" afterwards, and a promise that resolves
   * identically on a 409 makes that sentence a lie. Ignoring the value keeps the old behaviour exactly.
   */
  dispatch(command: Command | readonly Command[]): Promise<boolean>;
  /**
   * Undo the change at `seq`. Resolves to null on success, or the server's refusal.
   *
   * Handed BACK rather than set on `error`, which every other action does. A refusal here is about one
   * row of a list — "a later change (#41) already builds on this one" — and the global banner sits behind
   * the modal where the click happened, so the one place the message is useful is the only place it would
   * not appear.
   */
  revert(seq: number): Promise<string | null>;
  /**
   * Undo YOUR most recent undoable change — the Ctrl-Z gesture, as a store action.
   *
   * Sets `error` on refusal rather than handing it back, which is the opposite of `revert` and for the
   * same reason `revert` does the other thing: this is issued from the page, where the banner is visible,
   * not from behind the modal that hides it.
   *
   * The row is resolved by ASKING THE SERVER, not from the ticker. `activity` is a display list — capped,
   * and carrying neither `revertable` nor the six refusal reasons — so choosing from it would mean
   * guessing, and a wrong guess spends a keystroke on a change the server refuses. One small request per
   * press is the honest cost of being exact.
   */
  undoMine(): Promise<void>;
  /**
   * Write a note — free-standing, or against an existing change — and hand back the refusal.
   *
   * Separate from `dispatch` for exactly `revert`'s reason and no other: both are issued from inside the
   * history modal, which sits in front of the global error banner, so a message set on `error` would be
   * hidden behind the surface that caused it. Everything else about it is `dispatch`.
   */
  note(command: Extract<Command, { type: 'addNote' | 'annotateEvent' }>): Promise<string | null>;
  /**
   * Restore the whole project to a recorded point, and hand back the refusal.
   *
   * Same contract as `revert` and `note`, and for the same reason: it is issued from inside the history
   * modal, which sits in front of the global error banner, so a message set on `error` would be hidden
   * behind the surface that caused it.
   *
   * It resyncs on success where `note` does not — this replaces everything, which is the opposite of
   * writing nothing the design holds.
   */
  restore(seq: number, confirmProjectName: string): Promise<string | null>;
  select(nodeId: string | null): void;
  dismissError(): void;
  /**
   * Dismiss the "somebody changed this" notice, and nothing else.
   *
   * Its own action rather than a second caller of `dismissError`, which clears the global banner: the two
   * sit in different places on screen and say unrelated things, so one Dismiss button clearing both means
   * a colleague's edit can vanish because you closed an error about something else — and you would never
   * know it had been there.
   */
  dismissConflict(): void;
  /** Dismiss the "somebody restored this project" banner, and nothing else. */
  dismissRestored(): void;
}

/* ------------------------------------------------------------------ */

let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let cidCounter = 0;
/** Events that arrive out of order are held until their predecessor lands. */
let eventBuffer: ChangeEvent[] = [];
/*
  One resync at a time, and one more if anything asked while it ran.

  Being far behind is the CHEAP case: past the server's replay bound it sends `resyncRequired` and this
  client does exactly one fetch. The expensive case is being a little behind, where the server replays
  every missed event as its own frame and both resync branches of `applyRemoteEvent` fire per frame. The
  unprojectable branch does not bump `seq`, so one rename near the front of a replay burst leaves every
  later frame looking like a gap — several hundred frames, several hundred resyncs, each of them two full
  project fetches, all in flight at once.

  The trailing re-run is not belt-and-braces; it restores what the coalescing would otherwise lose. One
  resync in place of many means far more frames land WHILE it is running, and those go into a buffer that
  only drains after a clean apply. Without the second pass they wait for the next live event, which on a
  quiet project is a screen that stays stale after the reload that was supposed to fix it.
*/
let resyncInFlight: { promise: Promise<void>; again: boolean } | null = null;
/*
  What this tab last told the room it was looking at — the WIRE's memory, not the app's.

  A reconnect builds a fresh connection on the server, with `selectedId: null`, and the client sent only
  `hello`. Nothing re-announced, because `select` is called from user gestures alone and did not remember
  what it had said. So after any network blip — a lid closed and reopened, a train tunnel — colleagues saw
  you present and looking at nothing, until your next click. A cursor that recovers on the next gesture
  would be tolerable; a lens somebody reads to know where you are, and that is silently wrong for as long
  as you are reading rather than clicking, is not.

  Module-level rather than store state on purpose: no component renders it, and putting it in the store
  would re-render the tree on every selection for a value only the socket reads.
*/
let announcedSelection: string | null = null;

/**
 * The slice the handler reads, built from what this client holds.
 *
 * The roster comes from the fetched design rather than the narrow `/state` payload, so `isLeaver` and
 * the level values are the real ones — a leaver marked by a data update is excluded from a bulk move
 * here for the same reason the server excludes them, instead of the client optimistically including
 * someone the server will then drop.
 *
 * Still lossy: a bucket map carries no stage attribution or stranded plan, so a handler needing more
 * will reject and the caller resyncs rather than guessing — see projectDecisionWrites' `unprojected`.
 */
function sliceFrom(state: AppState): CommandSlice {
  const employees: Record<string, SliceEmployee> = Object.create(null);
  for (const e of rosterOf(state)) employees[e.id] = e;
  /*
    Planned hires too. `personOf` looks in `employees`, then `buildUps`, then throws — and `setBucket`
    declares both — so omitting them made the optimistic path reject every bucket change aimed at a
    hire with "No such employee: bu-…", and `setBucket` returns before sending anything. That is the
    only per-person side control a hire has. It was merely unreachable before this milestone, because
    a build-up could not be selected at all; making them selectable turned it into a visible dead
    button.
  */
  const buildUps: NonNullable<CommandSlice['buildUps']> = Object.create(null);
  for (const b of state.design?.design.buildUps ?? []) buildUps[b.id] = b;
  return {
    scenarioId: state.project?.activeScenarioId ?? null,
    employees,
    buildUps,
    decisions: decisionsFromBuckets(state.decisions),
  };
}

/**
 * The people this client knows about.
 *
 * The design is authoritative once it has arrived, and falls back to `/state`'s roster only in the
 * window before it does — which is short but real, because the two payloads are fetched together and
 * either can be the slower one.
 */
function rosterOf(state: AppState): SliceEmployee[] {
  if (state.design) return state.design.employees;
  return (state.project?.employees ?? []).map((e) => ({
    id: e.id,
    name: e.name,
    dedication: e.dedication,
    fte: e.fte,
    salary: e.salary,
    openPosition: e.openPosition,
    inactive: e.inactive,
    isLeaver: false,
  }));
}

/*
  Persisting the filter, without a request per keystroke.

  Deliberately fire-and-forget: a preference that fails to save is a filter you have to set again, not
  a change you think landed and did not. Surfacing it as an error banner would put a red box on screen
  for something with no consequence, next to the filter still visibly applied.
*/
let prefsTimer: ReturnType<typeof setTimeout> | null = null;
const PREFS_DEBOUNCE_MS = 500;

function queuePrefsSave(get: () => AppState): void {
  if (prefsTimer) clearTimeout(prefsTimer);
  prefsTimer = setTimeout(() => {
    prefsTimer = null;
    const projectId = get().project?.projectId;
    if (!projectId) return;
    void api.savePrefs(projectId, persistedLens(get().lens)).catch(() => {});
  }, PREFS_DEBOUNCE_MS);
}

export const useStore = create<AppState>((set, get) => ({
  user: null,
  assistantAvailable: false,
  voiceAvailable: false,
  realtimeAvailable: false,
  bootstrapped: false,
  projects: [],

  project: null,
  design: null,
  nodeDisplay: null,
  pptxGeom: null,
  scenarioName: null,
  lens: { ...lensForPhase('data'), filters: [], viewMode: 'hide', viewLayout: 'grouping', viewGrouping: [] },
  phase: 'data',
  decisions: {},
  seq: 0,
  role: null,

  members: [],
  connectionId: null,
  status: 'idle',
  activity: [],
  pending: {},
  error: null,
  notice: null,
  denied: null,
  conflict: null,
  restored: null,

  async bootstrap() {
    try {
      const { user, assistant } = await api.me();
      set({
        user,
        assistantAvailable: assistant?.available ?? false,
        voiceAvailable: assistant?.voice ?? false,
        realtimeAvailable: assistant?.realtime ?? false,
        bootstrapped: true,
      });
      await get().loadProjects();
    } catch {
      set({
        user: null,
        assistantAvailable: false,
        voiceAvailable: false,
        realtimeAvailable: false,
        bootstrapped: true,
      });
    }
  },

  async login(email, password) {
    set({ error: null });
    try {
      await api.login(email, password);
      /*
        BOOTSTRAP, NOT `set({ user })`, AND THIS WAS A LIVE DEFECT.

        `/api/auth/login` answers with the user and nothing else. The DEPLOYMENT FACTS — whether this
        tenant has an assistant, whether it has a speech model — come from `/api/me`, which only
        `bootstrap` reads, and on a cold load that runs BEFORE anybody has signed in: it 401s, lands in
        the catch, and sets both flags false. Signing in through the form then set `user` and left them
        exactly there.

        So the assistant and the microphone were invisible for the whole of a freshly signed-in
        session, and appeared only if the person happened to reload. Nothing caught it because every
        test either sets the store directly or mocks `me` into a bootstrap that has already succeeded —
        the defect lives in the DIFFERENCE between the two paths into a session, and the fix is to stop
        having two. It was found by the first end-to-end check ever pointed at the assistant.

        The cost is one extra request at sign-in: the same one the reload would have made.
      */
      await get().bootstrap();
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Could not sign in' });
      throw err;
    }
  },

  async logout() {
    get().closeProject();
    await api.logout();
    set({ user: null, projects: [], project: null, decisions: {}, activity: [] });
  },

  applyOwnProfile(user) {
    set({ user });
  },

  /*
    ARCHIVED ONES INCLUDED, and partitioned on screen rather than at the fetch.

    The alternative is a second request behind a toggle, which buys nothing: an archived project
    carries the same summary row as any other, the list is small, and `archived` is already on it. A
    filter over what is in hand cannot show a stale count, and — the part that matters — an archived
    project stays REACHABLE. Un-archiving is a command issued from inside the project, so a list that
    could not open one would be a one-way door.
  */
  async loadProjects() {
    const { projects } = await api.projects({ includeArchived: true });
    set({ projects });
  },

  async importProject(file, name) {
    set({ error: null, notice: null });
    try {
      const result = await api.importProject(file, { name });
      await get().loadProjects();

      /*
        A GUESSED MAPPING IS SAID OUT LOUD, once, at the only moment anybody will act on it.

        A project export carries a mapping somebody already confirmed, so there is nothing to report.
        A plain HR extract does not: the importer chose which column meant what, and the fields it
        placed on a weak signal are the ones that quietly read the wrong column. Saying so here is
        the difference between a mapping somebody checks and one they discover in the numbers.
      */
      if (result.source === 'roster') {
        const parts = [`Read ${result.employeeCount} people from a plain spreadsheet.`];
        if (result.lowConfidence.length) {
          parts.push(`Check the mapping for: ${result.lowConfidence.join(', ')}.`);
        }
        if (result.unmapped.length) {
          parts.push(`Not mapped: ${result.unmapped.join(', ')}.`);
        }
        parts.push('Review it under Data → Review column mapping.');
        set({ notice: parts.join(' ') });
      }
      return result.projectId;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Import failed' });
      throw err;
    }
  },

  async openProject(projectId) {
    get().closeProject();
    set({ status: 'connecting', error: null, denied: null });

    const [state, design, prefs] = await Promise.all([
      api.projectState(projectId),
      api.projectDesign(projectId),
      api.prefs(projectId),
    ]);
    set({
      project: state,
      design: design.state,
      nodeDisplay: design.nodeDisplay,
      pptxGeom: design.pptxGeom,
      scenarioName: design.scenarioName,
      decisions: state.decisions,
      seq: state.seq,
      role: state.role,
      // Every project opens on Data, as the app does: it is where you check that what you imported is
      // what you meant before any of it is priced.
      phase: 'data',
      // Filters come from the preferences row, so a reload lands you where you left off. They are
      // fetched alongside rather than inside the design: per-user state does not belong in a payload
      // every member of the project receives.
      // `activeScenarioId` is a preference but not a lens: the design payload is already the scenario it
      // names, so carrying it here would be a second copy of a fact `project` holds.
      lens: { ...lensForPhase('data'), ...persistedPrefsOf(prefs) },
      activity: [],
      pending: {},
      conflict: null,
      restored: null,
    });

    connect(projectId, set, get);
    // Not awaited: the project is usable the moment the design lands, and the ticker filling a beat later
    // is the right trade against holding the whole open on a list nobody has looked at yet.
    void backfillActivity(projectId, set, get);
  },

  setLens(next) {
    // A lens change is not a command: it emits no event, nobody else sees it, and it is the fourth place
    // this codebase had to stop reaching for ambient view state to decide something substantive.
    set((s) => ({ lens: { ...s.lens, ...next } }));
    // Debounced, because each editor applies a whole draft at once and a phase click carries these
    // across unchanged — there is nothing to save per keystroke.
    if (PERSISTED_LENS_KEYS.some((key) => key in next)) queuePrefsSave(get);
  },

  async setScenario(scenarioId) {
    const projectId = get().project?.projectId;
    if (!projectId || get().project?.activeScenarioId === scenarioId) return;

    /*
      The preference first, then the read — so a refetch that lands after a reload agrees with the one
      happening now. The order also means a failed save leaves nothing half-switched: the catch below
      reports it and the tab stays where it was.
    */
    await api.savePrefs(projectId, { activeScenarioId: scenarioId });
    const [fresh, design] = await Promise.all([
      api.projectState(projectId),
      api.projectDesign(projectId, scenarioId),
    ]);
    // Somebody closed or switched the project while this was in flight; its answer is about a tab that
    // is no longer here. The same guard `runResync` makes, for the same reason.
    if (get().project?.projectId !== projectId) return;

    set({
      project: fresh,
      design: design.state,
      nodeDisplay: design.nodeDisplay,
      pptxGeom: design.pptxGeom,
      scenarioName: design.scenarioName,
      /*
        The server's decisions, WITHOUT the optimistic overlay — unlike a resync, and deliberately.

        `pending` holds commands issued against the scenario being left; laying them over this one's
        decisions would show a change here that was made somewhere else. They stay in `pending` so their
        ack or rejection still resolves normally; they are simply not part of what this scenario looks
        like. (A command still un-acked when you switch straight back is briefly invisible. The ack is
        one round trip, and inventing a per-scenario overlay to cover that window would be a second
        model of pending state.)
      */
      decisions: fresh.decisions,
      seq: fresh.seq,
      // Both are statements about the design that was on screen a moment ago, and neither survives a
      // change of design. Left standing they would describe a scenario the reader is no longer in.
      conflict: null,
      restored: null,
    });
    announceSelection(get);
  },

  setPhase(phase) {
    const design = get().design;
    // A phase that is no longer visible cannot stay selected — turning the optional Target phase off
    // while sitting in it would otherwise leave the tab on a panel for something that does not exist.
    const next = design ? resolvePhase(design, phase) : phase;
    // The lens follows the phase and remains freely changeable afterwards: this sets the natural view
    // for the work the phase describes, it does not pin it.
    //
    // Filters are carried across explicitly. A phase decides where you are looking FROM; it has no
    // opinion about who you have narrowed to, and clearing that silently on a "Next" click would be
    // the same class of invisible loss this milestone exists to close.
    set((s) => ({ phase: next, lens: { ...lensForPhase(next), ...persistedLens(s.lens) } }));
  },

  closeProject() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    reconnectAttempt = 0;
    eventBuffer = [];
    /*
      The in-flight resync belongs to the project being closed, and so does its queued trailing re-run.
      Dropping the whole entry — rather than only the flag, which is all this used to do — is what stops
      a still-running fetch for the old project from blocking the new one's, and stops its `finally` from
      queueing a third.
    */
    resyncInFlight = null;
    announcedSelection = null;
    if (socket) {
      // Clear the handler first so the deliberate close does not schedule a reconnect.
      socket.onclose = null;
      socket.close();
      socket = null;
    }
    set({
      project: null,
      design: null,
      nodeDisplay: null,
      pptxGeom: null,
      decisions: {},
      members: [],
      status: 'idle',
      connectionId: null,
      pending: {},
      conflict: null,
      restored: null,
      denied: null,
      /*
        The banners go with the project too, which neither this nor `openProject` used to do for
        `notice`.

        Every one of them is a statement ABOUT a project — "Reloaded the project, it had moved on while
        you were away", a rejected command, a change that did not reach the server — so carrying one into
        a different project is asserting it about the wrong thing. `error` was cleared on the way IN by
        `openProject` and so was mostly invisible; `notice` was cleared by neither, and survived until
        somebody happened to press Dismiss.
      */
      error: null,
      notice: null,
    });
    clearProbe();
  },

  setBucket(employeeId, bucket) {
    const state = get();
    const project = state.project;
    if (!project) return;
    if (!state.role || !canWrite(state.role)) {
      set({ error: 'Your role on this project is read-only' });
      return;
    }

    const command: Command = {
      type: 'setBucket',
      scenarioId: project.activeScenarioId,
      employeeId,
      bucket,
      src: 'manual',
    };

    // Apply locally with the same reducer the server will run, so the UI responds immediately and
    // cannot disagree with the server about what the command means.
    let projected;
    try {
      const result = applyCommand(sliceFrom(state), command, {
        scenarioId: project.activeScenarioId,
      });
      projected = projectDecisionWrites(state.decisions, result.writes);
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Invalid change' });
      return;
    }

    const cid = `c${++cidCounter}`;
    set({
      decisions: projected.decisions,
      pending: { ...state.pending, [cid]: { command, rollbackTo: state.decisions } },
    });

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ t: 'cmd', cid, command }));
    } else {
      // No socket: still send it. Autosave must not depend on the realtime channel being up.
      void api
        .sendCommand(project.projectId, command)
        .then(({ event }) => {
          set((s) => {
            const { [cid]: _done, ...rest } = s.pending;
            return { pending: rest, seq: Math.max(s.seq, event.seq), activity: [event, ...s.activity].slice(0, 50) };
          });
        })
        .catch((err: unknown) => {
          rollback(cid, err instanceof Error ? err.message : 'Change rejected', set, get);
        });
    }
  },

  /**
   * Send any command and take the server's answer.
   *
   * Deliberately not optimistic, unlike `setBucket`. A bucket change has a local projection the client
   * can compute exactly — one entry in a map — but most commands do not: renaming a group rewrites every
   * head and placement keyed to the old id, a level reorder re-paths the whole tree, a data update
   * replaces the population. Guessing at those would be a second implementation of the handler, and the
   * ones worth guessing at are exactly the ones a guess gets wrong.
   *
   * So: send it, wait, refetch. That costs a round trip on an action a consultant takes a few times an
   * hour, and it buys the guarantee that what is on screen is what the server holds.
   */
  async dispatch(command) {
    const state = get();
    const project = state.project;
    if (!project) return false;
    if (!state.role || !canWrite(state.role)) {
      set({ error: 'Your role on this project is read-only' });
      return false;
    }

    // An array is one atomic change, not a loop: the server runs it in one transaction under one
    // batch id, each command seeing the previous one's writes, and undo takes the whole thing off.
    const batch = Array.isArray(command) ? command : null;
    // An empty batch changed nothing, which is not the same as failing to change something.
    if (batch && batch.length === 0) return true;

    set({ error: null });
    try {
      const events = batch
        ? (await api.sendCommands(project.projectId, batch)).events
        : [(await api.sendCommand(project.projectId, command as Command)).event];
      // Record the seqs here so the socket's copies of these same events are recognised as already
      // folded in rather than treated as gaps that buffer everything behind them.
      set((s) => ({
        seq: events.reduce((n, e) => Math.max(n, e.seq), s.seq),
        activity: [...events].reverse().concat(s.activity).slice(0, 50),
      }));
      await resync(project.projectId, set, get);
      return true;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Change rejected' });
      return false;
    }
  },

  /**
   * Undo a change, by asking the server to run the inverse its handler recorded.
   *
   * Deliberately NOT optimistic, unlike `setBucket`. Three of the reasons a revert is refused are only
   * knowable at the server — whether somebody built on the change since, whether it is already undone,
   * whether the batch it belongs to holds something irreversible — so a client that pre-applied it would
   * be unwinding a refusal most of the time it mattered. It is also not a frequent action.
   *
   * The seq bookkeeping is `dispatch`'s, and for the same reason: the route publishes the revert to every
   * client INCLUDING this one (there is no excluded connection, because a revert is not somebody's
   * optimistic apply), so without recording the seqs here the socket's copies would look like a gap and
   * buffer everything behind them.
   *
   * Then resync, because a revert's events are the whole catalogue: a bulk restore or a settings patch is
   * not projectable from a bucket map, and `applyRemoteEvent` would resync anyway on the first write it
   * could not place.
   */
  async revert(seq) {
    const state = get();
    const project = state.project;
    if (!project) return 'No project is open';
    if (!state.role || !canWrite(state.role)) return 'Your role on this project is read-only';

    try {
      const result = await api.revert(project.projectId, seq);
      set((s) => ({
        seq: result.events.reduce((n, e) => Math.max(n, e.seq), s.seq),
        activity: [...result.events].reverse().concat(s.activity).slice(0, 50),
      }));
      await resync(project.projectId, set, get);
      return null;
    } catch (err) {
      return err instanceof Error ? err.message : 'The change could not be undone';
    }
  },

  async undoMine() {
    const state = get();
    const project = state.project;
    if (!project || !state.role || !canWrite(state.role)) return;

    let events;
    try {
      ({ events } = await api.history(project.projectId, UNDO_SEARCH_LIMIT));
    } catch {
      set({ error: 'Could not reach the change log to find your last change.' });
      return;
    }
    if (get().project?.projectId !== project.projectId) return;

    /*
      Mine, newest first, and only what the server says can actually be taken back.

      `/history` is newest-first already, and `blocked` carries the six reasons a row cannot be reverted —
      undone, irreversible, behind a restore, nothing to undo, a dead scenario, owner-only, stale and
      destructive. Skipping past a blocked row rather than stopping at it is the point: after undoing a
      change and then undoing again, the first one is `undone` and the gesture should reach the one before
      it, which is what a person pressing Ctrl-Z twice means.
    */
    const mine = events.find(
      (e) => e.actorUserId === state.user?.id && e.blocked === null && e.inverse !== null,
    );
    if (!mine) {
      set({ error: 'Nothing of yours left to undo.' });
      return;
    }

    const refusal = await get().revert(mine.seq);
    // The supersede guard runs at revert time and cannot be predicted, so a refusal here is ordinary
    // rather than a bug — and it names the person who built on the change, which is who to talk to.
    if (refusal) set({ error: refusal });
  },

  async restore(seq, confirmProjectName) {
    const state = get();
    const project = state.project;
    if (!project) return 'No project is open';
    // Said here as well as refused by the server, because the modal shows the control only to an owner
    // and anybody else reaching this has hit a race — a role change while the modal was open.
    if (state.role !== 'owner') return 'Only the project owner can restore a project';

    try {
      await api.sendCommand(project.projectId, {
        type: 'restoreProject',
        seq,
        confirmProjectName,
      });
      /*
        The restorer's OWN tab refetches too.

        The hub excludes the originating connection from its publish, so this tab never receives the
        event that makes every other tab resync — and this is the one command where "I know what I did"
        is not enough to reconstruct the screen from. It replaced the roster, the scenarios and every
        design at once, from a picture only the server holds.
      */
      await resync(project.projectId, set, get);
      return null;
    } catch (err) {
      return err instanceof Error ? err.message : 'The project could not be restored';
    }
  },

  async note(command) {
    const state = get();
    const project = state.project;
    if (!project) return 'No project is open';
    // Said here as well as refused by the server, because the modal shows the composer only to a writer
    // and a reader who reaches this has hit a race — a role change while the modal was open.
    if (!state.role || !canWrite(state.role)) return 'Your role on this project is read-only';

    try {
      const { event } = await api.sendCommand(project.projectId, command);
      set((s) => ({
        seq: Math.max(s.seq, event.seq),
        activity: [event, ...s.activity].slice(0, 50),
      }));
      /*
        No resync, deliberately, and it is the one thing that differs from `dispatch`.

        Neither note command writes anything the design holds: `addNote` has no writes at all, and
        `annotateEvent` writes a projection the design does not read. Refetching the whole project to
        learn that nothing moved would be a round trip for nothing — and the modal reloads the history
        itself off the seq this just bumped, which is where the note actually appears.
      */
      return null;
    } catch (err) {
      return err instanceof Error ? err.message : 'The note could not be saved';
    }
  },

  select(nodeId) {
    announcedSelection = nodeId;
    announceSelection(get);
    // A notice about the node you just left is noise on the one you just opened.
    if (get().conflict !== null) set({ conflict: null });
  },

  dismissError() {
    set({ error: null, notice: null });
  },

  dismissConflict() {
    set({ conflict: null });
  },

  dismissRestored() {
    set({ restored: null });
  },
}));

/* ------------------------------------------------------------------ */

type Set = (partial: Partial<AppState> | ((s: AppState) => Partial<AppState>)) => void;
type Get = () => AppState;

function rollback(cid: string, message: string, set: Set, get: Get): void {
  const entry = get().pending[cid];
  set((s) => {
    const { [cid]: _rejected, ...rest } = s.pending;
    return {
      pending: rest,
      // Restore the snapshot taken before the optimistic apply. Safe because commands are applied
      // one at a time against the latest state, so the snapshot is never stale by more than this one.
      decisions: entry ? entry.rollbackTo : s.decisions,
      error: message,
    };
  });
}

/**
 * Take the server's state as truth, for both payloads.
 *
 * Called whenever this client learns that something changed it cannot reproduce locally. Both payloads
 * go together on purpose: `/state` carries the authoritative decisions and sequence, `/design` carries
 * everything else, and taking one without the other leaves the chart and the panels disagreeing.
 *
 * The project-id guard matters because this is always racing something — a response that lands after the
 * user closed the project, or opened a different one, must not be written into the store.
 */
function resync(projectId: string, set: Set, get: Get): Promise<void> {
  /*
    COALESCED, and the entry it coalesces onto now belongs to the project in front of you.

    The defect was not here but in `closeProject`, which cleared the trailing FLAG and left the in-flight
    promise. So the first resync of a newly opened project found the previous project's still running,
    set the flag, and returned that promise — B never fetched anything, while its caller
    (`applyRemoteEvent`'s catch, `dispatch`, `restore`) had every reason to believe it had. The trailing
    re-run then fired with the OLD `projectId`, captured in that closure, and was thrown away by
    `runResync`'s own project-id guard. Two round trips spent learning nothing, and project B left on
    stale state with no error anywhere.

    NO PER-PROJECT KEY ON THIS CHECK, and that was tried and removed. Guarding it on
    `entry.projectId === projectId` reads as the obvious fix and is unreachable: `openProject` calls
    `closeProject` first, and every caller passes the CURRENT project's id, so an entry for another
    project can never be in the slot when one is requested. Mutation testing said so — restoring the old
    unkeyed condition failed nothing once the clearing was in place — and a guard no test can reach is
    the thing this file has already been caught carrying twice.
  */
  const current = resyncInFlight;
  if (current) {
    current.again = true;
    return current.promise;
  }

  const promise = runResync(projectId, set, get).finally(() => {
    /*
      Only if this is still the entry in the slot. A project switch nulls it, and a resync for another
      project replaces it — in both cases this run's trailing re-fetch belongs to a project nobody is
      looking at, and `runResync` would throw its answer away.
    */
    if (resyncInFlight?.promise !== promise) return;
    const again = resyncInFlight.again;
    // Cleared BEFORE the trailing call, or that call would find itself in flight and only set the flag.
    resyncInFlight = null;
    if (again) void resync(projectId, set, get);
  });
  resyncInFlight = { promise, again: false };
  return promise;
}

async function runResync(projectId: string, set: Set, get: Get): Promise<void> {
  /*
    Anything buffered is superseded by what this fetch is about to return, and keeping it is worse than
    dropping it: a frame held against a predecessor the resync has just consumed can never be drained.
    `welcome.resyncRequired` already does exactly this, for exactly this reason.
  */
  eventBuffer = [];
  const [fresh, design] = await Promise.all([
    api.projectState(projectId),
    api.projectDesign(projectId),
  ]);
  if (get().project?.projectId !== projectId) return;
  set((s) => {
    const next: AppState = { ...s, project: fresh, design: design.state };
    /*
      The orphans are settled HERE because this is the one moment the server's truth is in hand, and
      before `replayPending` runs — which would otherwise lay the very commands being judged back on top
      of the evidence used to judge them.
    */
    const settled = settleOrphaned(next, fresh.decisions);
    const withoutOrphans: AppState = { ...next, pending: settled.kept };
    return {
      project: fresh,
      design: design.state,
      nodeDisplay: design.nodeDisplay,
      pptxGeom: design.pptxGeom,
      scenarioName: design.scenarioName,
      // The server's decisions, with anything still in flight laid back on top. Without this a resync
      // triggered while a bucket change is un-acked would silently revert it on screen and never put it
      // back: the ack only clears the pending entry, it does not re-apply the change.
      decisions: replayPending(withoutOrphans, fresh.decisions),
      // What "undo this optimistic change" means has moved with the server's state, so the snapshots
      // move with it. Still one-at-a-time reasoning — see rollback().
      pending: Object.fromEntries(
        Object.entries(settled.kept).map(([cid, entry]) => [cid, { ...entry, rollbackTo: fresh.decisions }]),
      ),
      /*
        Said, not swallowed. A change that did not reach the server is the defect this milestone is
        about, and dropping it quietly would be the same silence in a new place. Kept off `notice`,
        which the reconnect's own happy path writes.
      */
      error: settled.lost.length
        ? `${settled.lost.length === 1 ? 'One change' : `${settled.lost.length} changes`} did not reach `
          + 'the server before the connection dropped, and have been undone. Make them again.'
        : s.error,
      /*
        THE SERVER'S NUMBER, not the higher of the two.

        This used to be `Math.max(s.seq, fresh.seq)`, which refuses to move the sequence backwards — and
        a client ahead of the server is precisely the case the server sends `resyncRequired` for, so the
        one path written to make this tab take the server's truth was the path that could not. The cost
        was total and silent: every later frame is dropped by the `event.seq <= state.seq` guard at the
        top of `applyRemoteEvent`, with `status: 'live'` and a screen that has quietly stopped updating.

        Moving backwards is cheap by comparison. A frame applied between `/state` being read and its
        response landing is overwritten by `fresh.decisions` either way — the max never protected the
        decisions, only the number — and the next frame then reads as a gap and resyncs again. One extra
        round trip against a tab that never recovers.
      */
      seq: fresh.seq,
      role: fresh.role,
      // A phase can stop existing between one fetch and the next — someone else turning the optional
      // Target phase off is a command, and this tab may be sitting in it.
      phase: resolvePhase(design.state, s.phase),
    };
  });
}

/**
 * Settle the commands that were in flight on a socket that died, against what the server actually holds.
 *
 * BY COMPARISON, NOT BY A CLOCK, and that distinction is the whole design. A timeout cannot tell "the
 * server never saw it" from "the ack was slow", and guessing wrong is unrecoverable here: this tab never
 * receives its own event — `publishEvent` excludes the originating connection, which `realtime.test.ts`
 * asserts — so the ack is the only live carrier of "your change landed". A rollback fired on a command
 * that had in fact committed would leave the screen permanently disagreeing with the server, because the
 * late ack advances `seq` past the event without restoring the decision and every repair path is then
 * shut: `applyRemoteEvent` drops it on the seq guard, and a reconnect replays only what is strictly
 * later.
 *
 * The server's own state answers the question exactly. Re-applying the command to the freshly fetched
 * decisions and finding it changes nothing means it already landed; finding it changes something means
 * it did not. No stopwatch, and no window in which the answer is a guess.
 *
 * Returns the commands that did NOT land, so the caller can say so rather than dropping them in silence.
 */
function settleOrphaned(
  state: AppState,
  fresh: Record<string, Bucket>,
): { kept: Record<string, PendingCommand>; lost: Command[] } {
  const kept: Record<string, PendingCommand> = {};
  const lost: Command[] = [];
  for (const [cid, entry] of Object.entries(state.pending)) {
    if (!entry.orphaned) {
      kept[cid] = entry;
      continue;
    }
    if (!landed(state, fresh, entry.command)) lost.push(entry.command);
  }
  return { kept, lost };
}

/** True when applying this command to the server's decisions would change nothing — so it is already in. */
function landed(state: AppState, fresh: Record<string, Bucket>, command: Command): boolean {
  try {
    const result = applyCommand(
      { ...sliceFrom(state), decisions: decisionsFromBuckets(fresh) },
      command,
      { scenarioId: scenarioIdOf(command) ?? '' },
    );
    const projected = projectDecisionWrites(fresh, result.writes);
    if (projected.unprojected.length) return false;
    const keys = new Set([...Object.keys(fresh), ...Object.keys(projected.decisions)]);
    for (const key of keys) if (fresh[key] !== projected.decisions[key]) return false;
    return true;
  } catch {
    // If it cannot even be applied against the server's state, it certainly did not land as sent.
    return false;
  }
}

/** Re-apply the commands this client has sent but not yet had acknowledged. */
function replayPending(state: AppState, base: Record<string, Bucket>): Record<string, Bucket> {
  let decisions = base;
  for (const { command } of Object.values(state.pending)) {
    try {
      const result = applyCommand({ ...sliceFrom(state), decisions: decisionsFromBuckets(decisions) }, command, {
        scenarioId: scenarioIdOf(command) ?? '',
      });
      const projected = projectDecisionWrites(decisions, result.writes);
      if (!projected.unprojected.length) decisions = projected.decisions;
    } catch {
      // Its ack or its rejection settles it; a resync is not the place to decide a command's fate.
    }
  }
  return decisions;
}

function applyRemoteEvent(event: ChangeEvent, set: Set, get: Get): void {
  const state = get();
  const project = state.project;
  if (!project || event.projectId !== project.projectId) return;

  // Ignore anything already folded in, so a replay after reconnect is idempotent.
  if (event.seq <= state.seq) return;

  /*
    ATTRIBUTION FIRST, before either branch takes over.

    Every event reaching here is from another CONNECTION — the hub publishes with the originating one
    excluded — so this is by construction "somebody else changed something", which is why no
    `actorUserId !== me` test is needed or would be right: your own second tab moving a person under you
    is the same surprise, and arguably the more confusing one.

    Read off `touched`, which the server recorded when it applied the change, rather than re-derived from
    the command. `touchesNode` and the revert guard's `keysOverlap` are the same module in core, beside
    the handlers that write the keys, so there is one vocabulary rather than two that drift.
  */
  if (announcedSelection && touchesNode(event.touched, announcedSelection)) {
    set({
      conflict: {
        actorName: event.actorName || 'Someone',
        summary: event.summary,
        seq: event.seq,
        nodeId: announcedSelection,
      },
    });
  }

  /*
    A RESTORE, which is the one event that is not a change to something.

    It reaches this tab as an ordinary event and converges correctly at the state level: the handler needs
    slices no client holds, so it throws, takes the catch branch below and refetches. What it did not do
    was say anything — `resync` sets no notice, and the only "this reloaded under you" sentence in the
    product is the reconnect one. So somebody else's restore replaced this tab's whole project in silence,
    with the status still reading "live".

    Read off the event's TYPE rather than its `touched`, and that is the whole reason `restoreProject`
    records `touched: []`: that vocabulary names individual things and a restore touches all of them, so
    there is no key to match on. `keysOverlap` is exact by design, and giving it a wildcard would make the
    key format mean two different kinds of thing.

    It clears `conflict`, which would otherwise sit underneath saying somebody changed the node you have
    open — true, but the smaller half of the news, and two banners about one event read as two events.
  */
  if (event.type === 'restoreProject') {
    set({
      restored: {
        actorName: event.actorName || 'Someone',
        summary: event.summary,
        seq: event.seq,
      },
      conflict: null,
    });
  }

  if (event.seq > state.seq + 1) {
    /*
      A gap. Held for the drain below, AND resynced — because over a single socket frames are ORDERED, so
      a gap does not mean "arrived early", it means one was missed, and its predecessor is never coming.

      Waiting for it stranded the tab permanently and silently: the drain runs only after a successful
      apply (below), `resync` never touched the buffer, and every later frame was then also a gap and
      also buffered. No error, no banner, `status: 'live'`, and a screen that had quietly stopped
      updating. The window is the resync itself — a frame published after `/state` was read but before
      it returned — which P4m-g widened considerably by making far more events take the resync branch.
    */
    eventBuffer.push(event);
    void resync(project.projectId, set, get);
    return;
  }

  let decisions = state.decisions;
  try {
    const result = applyCommand(sliceFrom(state), event.command, {
      scenarioId: scenarioIdOf(event.command) ?? '',
    });
    /*
      No writes at all is not the same as nothing happened.

      This client evaluates the command against a FABRICATED slice: `decisionsFromBuckets` invents a
      decision row per bucket with `strandedAction: null` and `day2Exit: null`, because a bucket map
      carries neither. A handler with a no-op guard then compares against the invention and finds a
      match — so clearing a stranded plan, which is the inverse of every stranded plan and therefore
      every undo of one, produced zero writes here and a full write on the server. Zero writes means an
      empty `unprojected`, so the guard below never fired and the peer went on drawing a plan that had
      just been cleared.
    */
    if (result.writes.length === 0) throw new Error('nothing-to-project');
    const projected = projectDecisionWrites(decisions, result.writes);
    // A write this client cannot represent means its view is now incomplete, not merely behind.
    if (projected.unprojected.length) throw new Error('unprojectable');
    // Anything this tab has sent and not had acknowledged goes back on top; the remote event is the
    // server's older truth about the same map, and applying it bare silently discarded the local change.
    decisions = replayPending(state, projected.decisions);
  } catch {
    /*
      Either the handler needed a slice this client does not hold, or the command wrote something a
      bucket map cannot represent — a head, a rename, a TSA, a refreshed population. Both mean the same
      thing: this client's view is now incomplete rather than merely behind, so it takes the server's
      answer instead of diverging silently.

      The event still goes into the activity list. It happened, somebody did it, and dropping it because
      this client could not replay it would hide the change that prompted the reload.
    */
    set((s) => ({ activity: [event, ...s.activity].slice(0, 50) }));
    void resync(project.projectId, set, get);
    return;
  }

  set((s) => ({
    decisions,
    seq: event.seq,
    activity: [event, ...s.activity].slice(0, 50),
  }));

  // Drain anything that was waiting on this sequence number.
  const nextUp = eventBuffer.findIndex((e) => e.seq === event.seq + 1);
  if (nextUp >= 0) {
    const [queued] = eventBuffer.splice(nextUp, 1);
    if (queued) applyRemoteEvent(queued, set, get);
  }
}

/** The ticker's cap, and the same one every socket path already applies. */
const ACTIVITY_LIMIT = 50;

/**
 * Fill the Activity ticker with what happened before this tab opened.
 *
 * The ticker was socket-fed only, so it was EMPTY on page load and after every resync-required
 * reconnect — which is precisely the moment somebody wants to know what moved while they were away. Its
 * own comment already made that complaint about itself.
 *
 * FROM `/history`, NOT `/events`, and the difference is a silent wrong answer rather than a preference.
 * `eventsSince` orders ASC and caps at 1000 with no cursor, so on a project past a thousand changes a
 * newest-first ticker filled from it would show changes 1 to 1000 — the import and nothing since — and
 * say nothing about the truncation. `/history` is newest-first and bounded by design, for the same reason
 * written up when that was fixed there.
 *
 * IT STILL DOES NOT SHOW `undone`, though `/history` carries it. A revert publishes its own event, so it
 * arrives as a new row above the one it took back: the feed reads as a true chronology of what people
 * did. Striking through an earlier row would make it a statement of current state instead, which is what
 * the history modal is for — and which needs the refusal reasons the modal fetches and this does not.
 *
 * Merged rather than assigned, because the socket may already have delivered rows while this was in
 * flight, and the project guard is the same one `runResync` needs: a response landing after the user
 * moved on must not be written into a different project's screen.
 */
async function backfillActivity(projectId: string, set: Set, get: Get): Promise<void> {
  let events;
  try {
    ({ events } = await api.history(projectId, ACTIVITY_LIMIT));
  } catch {
    // A ticker that could not be pre-filled is the state it was in before this existed. Nothing to say.
    return;
  }
  if (get().project?.projectId !== projectId) return;
  set((s) => {
    const byId = new Map<string, ChangeEvent>();
    for (const event of [...s.activity, ...events]) byId.set(event.id, event);
    return {
      activity: [...byId.values()].sort((a, b) => b.seq - a.seq).slice(0, ACTIVITY_LIMIT),
    };
  });
}

/** Tell the room where this tab is, if there is a room to tell. */
function announceSelection(get: Get): void {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  socket.send(
    JSON.stringify({
      t: 'presence',
      selectedId: announcedSelection,
      scenarioId: get().project?.activeScenarioId ?? null,
    }),
  );
}

/**
 * 1008, "policy violation" — the code the server uses for every deliberate refusal.
 *
 * Named rather than inlined because the number is meaningless at the call site and because the
 * distinction it draws is the load-bearing one: 1008 is a decision, 1006 is a wire.
 */
const POLICY_CLOSE = 1008;

/**
 * Exponential backoff with jitter, capped at thirty seconds.
 *
 * THE JITTER IS NOT DECORATION. Every tab on a project is disconnected by the same event — a server
 * restart, a deploy, a dropped uplink — so without it they all wake at the same millisecond and hit the
 * upgrade together, then do it again, in lockstep, for as long as the outage lasts. P5b fixed the
 * server's side of that stampede; this is the client's, and it was the last place a fixed schedule
 * survived. The spread is the full lower half of each step, so the retries of N tabs fan out across the
 * whole interval rather than clustering at its end.
 */
export function backoffDelay(attempt: number): number {
  const ceiling = Math.min(30_000, 500 * 2 ** attempt);
  return ceiling / 2 + Math.random() * (ceiling / 2);
}

/**
 * How long a liveness probe waits for its answer before the socket is declared dead.
 *
 * Longer than the server's 15-second lock timeout on purpose. Frames from one connection are handled
 * strictly in order, so a probe queued behind a bucket change that is itself waiting on the project row
 * lock can legitimately take that long to be answered. Anything shorter would drop healthy sockets under
 * exactly the contention that makes a tab feel stuck.
 */
export const PROBE_DEADLINE_MS = 20_000;

let probeTimer: ReturnType<typeof setTimeout> | null = null;

function clearProbe(): void {
  if (probeTimer !== null) {
    clearTimeout(probeTimer);
    probeTimer = null;
  }
}

/**
 * Ask the socket whether it is still there, once, because the browser just said the network moved.
 *
 * NOT A HEARTBEAT, and the distinction is the whole reason this is shaped as it is. Three places record
 * that "the web client sends no heartbeat of its own" — and they record it as the PREMISE for why the
 * server's sweep pings rather than as a ruling that a tab may never check on itself. This sends nothing
 * on a quiet connection and nothing on a timer; it asks exactly once, when the operating system reports
 * that the thing underneath the socket has changed.
 *
 * A POLLING WATCHDOG WAS CONSIDERED AND REJECTED, for reasons worth keeping:
 *   · A watch-only version cannot exist. RFC 6455 ping/pong are CONTROL frames, and the WebSocket API
 *     exposes open/message/close/error only — `message` fires for data frames. The browser answers the
 *     server's ping below the JS boundary, so a tab cannot observe the one signal that already proves
 *     its socket is alive.
 *   · "No frame for a while" is the healthy steady state. A consultant reading a project alone receives
 *     no data frames at all, indefinitely, over a perfectly good socket — so an idle-timer watchdog
 *     fires on quiet, which is most of the time.
 *   · An interval started per connection is the timer-lifetime bug this module has already shipped twice
 *     (`prefsTimer` and `resyncInFlight`, both uncleared on `closeProject`). `connect` is re-entered on
 *     every reconnect, so each bounce would add another.
 * The listeners below are registered ONCE at module scope for the same reason: nothing to leak per
 * project, per connection, or per reconnect.
 */
function probeLiveness(get: Get): void {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  if (probeTimer !== null) return; // one in flight is enough
  socket.send(JSON.stringify({ t: 'ping' }));
  const probed = socket;
  probeTimer = setTimeout(() => {
    probeTimer = null;
    // Still the same socket, still open, and it never answered: it is half-open, and `close` is what
    // starts the reconnect the tab would otherwise never make.
    if (socket === probed && probed.readyState === WebSocket.OPEN && get().status !== 'denied') {
      probed.close();
    }
  }, PROBE_DEADLINE_MS);
}

/**
 * The two moments the operating system tells a page its network has moved.
 *
 * Edge-triggered rather than polled, which is what makes this cheap: a lid closed and reopened, a train
 * tunnel, a dropped VPN, a laptop waking — the browser knows exactly when each happens and says so for
 * free. Those are also the dominant causes of a half-open socket, which is why a probe here is worth
 * more than a timer that has to guess.
 */
export function installLivenessListeners(get: Get): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => probeLiveness(get));
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') probeLiveness(get);
    });
  }
}

function connect(projectId: string, set: Set, get: Get): void {
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const ws = new WebSocket(`${scheme}://${window.location.host}/ws/projects/${projectId}`);
  socket = ws;

  /*
    NO `reconnectAttempt = 0` HERE, and its being here was the whole of the second defect.

    Every one of the server's refusals happens AFTER the upgrade completes — the route takes no
    preHandler, so the socket is accepted and then closed with 1008 (`apps/server/src/app.ts`). So a tab
    with no access still fires `onopen`, which reset the counter to zero; `onclose` then computed
    `500 * 2 ** 0` and set it to 1; and `reconnectAttempt > 1` was never true. The result was a retry
    every 500 ms for the life of the tab, with the pill reading "Reconnecting…" and never even reaching
    "Offline — retrying". The backoff was not merely too shallow: it could not grow at all.

    A connection is worth counting as successful when the SERVER says so, which is `welcome`.
  */
  ws.onopen = () => {
    ws.send(JSON.stringify({ t: 'hello', lastSeq: get().seq }));
  };

  ws.onmessage = (raw) => {
    /*
      ANY frame settles an outstanding probe, not just the pong. The question a probe asks is "is this
      socket carrying traffic", and a `presence` or an `event` answers it as well as a `pong` does —
      better, in fact, since it proves the server is doing work for this connection rather than only
      echoing.
    */
    clearProbe();
    const parsed = serverMessageSchema.safeParse(JSON.parse(String(raw.data)));
    if (!parsed.success) return;
    const message = parsed.data;

    switch (message.t) {
      case 'welcome': {
        // The backoff resets HERE rather than in `onopen`: an accepted-then-refused socket opens too.
        reconnectAttempt = 0;
        set({ status: 'live', connectionId: message.connectionId, members: message.members });
        // Say where we are again. The server's connection is new and starts on nothing, and this is the
        // only moment the client knows that — see `announcedSelection`.
        if (announcedSelection !== null) announceSelection(get);
        /*
          A resync is REQUIRED here too, whatever the server thinks. The server asks for one when this
          tab is too far behind to catch up by replay; it knows nothing about commands that died in
          flight, and settling those needs its state in hand. Without this the orphans survive a clean
          reconnect — which is the commonest way to reach them.
        */
        const orphaned = Object.values(get().pending).some((entry) => entry.orphaned);
        if (message.resyncRequired || orphaned) {
          eventBuffer = [];
          void resync(projectId, set, get).then(
            () => {
              set({ notice: 'Reloaded the project — it had moved on while you were away.' });
              // The same moment, and the same reason: the buffer was just cleared, so without this the
              // ticker is empty on exactly the reconnect that means somebody has been busy.
              void backfillActivity(projectId, set, get);
            },
            /*
              THE FAILURE BRANCH, which this had none of — and it is the third instance of one defect.

              `status: 'live'` is set four lines above, before the refetch is even attempted. If `/state`
              or `/design` rejects, this tab keeps its OLD higher seq, and every subsequent frame is then
              dropped by the `event.seq <= state.seq` guard in `applyRemoteEvent` — permanently, silently,
              with the pill reading "Live". P5b closed exactly this shape twice (the reconnect stampede
              and the client-ahead freeze); it survived here because a rejected fetch was never tried.

              Saying so is the whole fix: the tab is not live, and the person looking at it needs to
              reload rather than to keep working on a screen that has stopped moving. Not retried
              automatically — a refetch that failed once will usually fail again, and a silent retry loop
              is how this became invisible in the first place.
            */
            (err: unknown) => {
              set({
                status: 'offline',
                error:
                  'This project could not be reloaded, so what you are looking at may be out of date. '
                  + 'Refresh the page to try again.',
              });
              // Kept off the happy path's `notice`, which says the opposite thing.
              console.error('resync after reconnect failed', err);
            },
          );
        }
        return;
      }
      case 'event':
        applyRemoteEvent(message.event, set, get);
        return;
      /*
        SOMETHING HAPPENED AND NONE OF IT WAS YOURS: advance the watermark, render nothing.

        Only a member whose access covers part of the organisation ever receives this. Withholding the
        frame entirely would have been the obvious server-side fix and is the wrong one: `seq` is how
        this client knows whether it is behind, and a watermark that never moved would drift a scoped
        member into a forced full refetch on every reconnect.

        NOTHING GOES IN THE ACTIVITY TICKER. "Somebody edited a person you cannot see" is already more
        than the scope should admit, and a row that said it would be a notice about a population the
        payload was narrowed to exclude.
      */
      case 'skip':
        set((s) => ({ seq: Math.max(s.seq, message.seq) }));
        return;
      /*
        YOUR SLICE CHANGED AND THE FRAME COULD NOT BE SENT: refetch.

        The straddling case — a bulk edit naming three hundred people, forty of them yours. The server
        cannot send the event, because `summary` is prose naming all three hundred, and cannot stay
        silent, because forty of your people just moved. `resync` is the existing answer for "this
        client's view is now incomplete rather than merely behind", reached here deliberately rather
        than through a new path.
      */
      case 'stale':
        set((s) => ({ seq: Math.max(s.seq, message.seq) }));
        void resync(projectId, set, get);
        return;
      case 'ack': {
        set((s) => {
          const { [message.cid]: _acked, ...rest } = s.pending;
          return {
            pending: rest,
            seq: Math.max(s.seq, message.seq),
            /*
              YOUR OWN CHANGE, IN YOUR OWN FEED. The hub excludes the author from the broadcast, so this
              ack is the only place this event ever reaches the tab that made it — and without this the
              ticker was a log of what OTHER people had done, while calling itself Activity. Every other
              writer already did this: the socket-down fallback below, `dispatch`, `revert`, `note`, a
              colleague's event and the backfill.

              Prepended plainly, like every other writer. A dedupe by id was written here and removed:
              mutation testing found nothing depended on it, and there is no interleaving that needs it —
              the backfill merges `/history` by id already, and it runs on a welcome, which precedes any
              command on that connection. An ack from the previous connection died with it.
            */
            activity: [message.event, ...s.activity].slice(0, ACTIVITY_LIMIT),
          };
        });
        return;
      }
      case 'reject':
        rollback(message.cid, message.message, set, get);
        return;
      case 'presence':
        set({ members: message.members });
        return;
      case 'pong':
        return;
      /*
        The last frame this socket will get, and the reason a tab can now stop.

        THE FRAME RATHER THAN THE CLOSE CODE is what carries this, because the code is not a reliable
        channel: an abnormal close surfaces as 1006 whatever the server intended, which is exactly why
        `realtime.test.ts` accepts 1006 alongside 1008 for the upgrade refusals. The frame arrives before
        the close, says which of the two things happened, and carries the sentence to show for it.

        `members` is cleared with it: a presence roster for a project this tab has been thrown out of is
        a list of people it can no longer see.
      */
      case 'evicted':
        set({ status: 'denied', denied: message.message, members: [] });
        return;
    }
  };

  ws.onclose = (event) => {
    if (socket !== ws) return; // superseded by a newer socket
    socket = null;
    clearProbe();

    /*
      TWO REASONS NOT TO COME BACK, and they arrive by different routes.

      The `evicted` frame is the first: it has already set `denied` above, and this close is simply that
      eviction landing. The second is a reconnect that gets refused — the tab tries again, the upgrade
      completes, and the server closes it with 1008 having sent no frame at all ("No access to this
      project", "Not authenticated"). That is the case that actually spun, because it is where a tab
      thrown out of a deleted project ends up on its very next attempt.

      1008 rather than any-close, and the distinction is the whole point of reading the event: 1006 is
      what a dropped wire looks like, and a dropped wire is exactly what should keep retrying. Only a
      close the server chose deliberately means "stop".
    */
    if (get().status === 'denied') {
      set({ members: [] });
      return;
    }
    if (event.code === POLICY_CLOSE) {
      set({
        status: 'denied',
        members: [],
        denied:
          'You no longer have access to this project. It may have been deleted, or your access may have '
          + 'been removed. Go back to All projects.',
      });
      return;
    }

    // Back off, but keep trying: a dropped socket must not silently stop collaboration.
    const delay = backoffDelay(reconnectAttempt++);
    /*
      Anything still in flight went down with the socket. `cid` is scoped to one connection, so no later
      frame can ever settle these — they are marked here and judged against the server's state on the
      next resync, rather than left to sit in `pending` and be replayed over the truth forever.
    */
    set((s) => ({
      status: reconnectAttempt > 1 ? 'offline' : 'reconnecting',
      members: [],
      pending: Object.fromEntries(
        Object.entries(s.pending).map(([cid, entry]) => [cid, { ...entry, orphaned: true }]),
      ),
    }));
    /*
      No `status !== 'denied'` re-check here, and its absence is deliberate rather than an oversight.

      One was written, and mutation testing killed the comment rather than the code: removing the check
      failed nothing, because there is no interleaving that reaches it. A timer is only scheduled when
      neither refusal above fired, `socket` is null for as long as it is pending, and the only thing that
      sets `denied` is a frame — which needs a live socket. The guard was defending against a race that
      cannot happen, with a comment asserting one that can.
    */
    reconnectTimer = setTimeout(() => {
      if (get().project?.projectId === projectId) connect(projectId, set, get);
    }, delay);
  };

  ws.onerror = () => {
    // Not on a tab that has been told it is out: an error after an eviction is that eviction, and
    // overwriting the explanation with "reconnecting" would put the spinner back on a dead tab.
    if (get().status !== 'denied') set({ status: 'reconnecting' });
  };
}

/*
  Registered once, here, rather than per project or per connection.

  There is nothing to tear down and nothing to leak: no interval, no per-socket subscription, and the
  handler reads the current socket from the module when it fires. That is a deliberate answer to the two
  timer-lifetime bugs this file has already shipped — `prefsTimer` and `resyncInFlight`, both left
  running by `closeProject` — since the way to not leak a listener per project is to not have one.
*/
installLivenessListeners(useStore.getState as unknown as Get);
